import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import { Col, Row, Select } from 'antd';
import { useState } from 'react';

import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';

import GoogleMapsIcon from '../../assets/images/icons/Google_Maps_icon.png';
import { useTranslation } from 'react-i18next';

const DEFAULT_SEARCH_MIN_LENGTH = 3;

export const defaultAddressComponentsParser = [
  {
    key: 'number',
    componentType: 'street_number',
    nameSize: 'long_name'
  },
  {
    key: 'street',
    componentType: 'route',
    nameSize: 'long_name'
  },
  {
    key: 'postal_code',
    componentType: 'postal_code',
    nameSize: 'long_name'
  },
  {
    key: 'city',
    componentType: 'locality',
    nameSize: 'long_name'
  },

  {
    key: '"administrative_area_level_2"',
    componentType: '"administrative_area_level_2"',
    nameSize: 'long_name'
  },

  {
    key: 'country',
    componentType: 'country',
    nameSize: 'long_name'
  }
];

export const AddressAutocomplete = React.forwardRef(
  (
    {
      onSelect,
      onClear,
      searchMinLength = DEFAULT_SEARCH_MIN_LENGTH,
      addressComponentsParser = defaultAddressComponentsParser,
      withAddressPreview = false,
      disabled = false
    },
    ref
  ) => {
    const { t } = useTranslation();

    const { dispatchAPI } = useAuthContext();
    const { message } = useErrorMessage();

    const [value, setValue] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const [valueLocation, setValueLocation] = useState(null);

    const displayAddressPreview = useMemo(() => {
      return withAddressPreview && !!valueLocation?.lat && !!valueLocation?.lng;
    }, [withAddressPreview, valueLocation]);

    const addressPreviewLink = useMemo(() => {
      if (!displayAddressPreview) {
        return null;
      }

      const { lat, lng } = valueLocation;
      if (!lat || !lng) {
        return null;
      }

      return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat}0%2C${lng}&heading=-45&pitch=38&fov=80`;
    }, [displayAddressPreview, valueLocation]);

    useImperativeHandle(ref, () => ({
      clear: () => {
        handleClear();
      }
    }));

    const getPredictions = async (searchText) => {
      if (searchText.length < searchMinLength) {
        setPredictions([]);
        return;
      }

      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/${searchText}`
        });

        const predictions = data.predictions;

        if (!Array.isArray(predictions)) {
          setPredictions([]);
          return;
        }

        const mappedPredictions = predictions.map((prediction) => ({
          value: prediction.place_id,
          label: prediction.description
        }));

        setPredictions(mappedPredictions);
      } catch (error) {
        if (error.response) {
          message(error.response.status);
        }
      }
    };

    const parseAddressComponents = (addressComponents) => {
      const parsedAddressComponents = {};

      addressComponentsParser.forEach((parser) => {
        const { key, componentType, nameSize } = parser;

        const addressComponent = addressComponents.find((component) =>
          component.types?.some((type) => type === componentType)
        );

        if (addressComponent) {
          parsedAddressComponents[key] = addressComponent[nameSize];
        }
      });

      return parsedAddressComponents;
    };

    const handleSelect = async (placeId) => {
      if (!placeId) {
        return;
      }
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/placeid/${placeId}`
        });

        if (!data?.result) {
          return;
        }

        const {
          formatted_address,
          address_components,
          geometry: { location }
        } = data.result;

        setValueLocation(location);

        const parsedAddressComponents = {
          address_autocomplete: formatted_address,
          ...parseAddressComponents(address_components),
          ...location
        };

        if (typeof onSelect === 'function') {
          onSelect(parsedAddressComponents);
        }

        setValue(placeId);
      } catch (error) {
        if (error.response) {
          message(error.response.status);
        }
      }
    };

    const handleClear = () => {
      setValue(null);
      setPredictions([]);
      setValueLocation(null);

      if (typeof onClear === 'function') {
        onClear();
      }
    };

    return (
      <Row justify="end" gutter={10}>
        <Col span={24}>
          <Select
            value={value}
            options={predictions}
            onSearch={getPredictions}
            onSelect={handleSelect}
            onClear={handleClear}
            showSearch
            allowClear
            filterOption={false}
            showArrow={false}
            notFoundContent={null}
            disabled={disabled}
          />
        </Col>

        {displayAddressPreview && (
          <Row style={{ margin: '4px 0' }}>
            <a
              className="form-map-link"
              target="_blank"
              rel="noopener noreferrer"
              href={addressPreviewLink}
            >
              {t('contracts.form.autocomplete_address_preview')}
              <img src={GoogleMapsIcon} alt="GMapsIcn" />
            </a>
          </Row>
        )}
      </Row>
    );
  }
);
